import { useEffect, useState } from "react";

import { supabase } from "@/integrations/supabase/client";
import { useLocale } from "@/i18n/useLocale";

/**
 * Spoken recaps exist per language. Readers on /no or /zh-hant hear their own
 * language; if that file is not ready yet they keep the English one so the
 * player never goes silent.
 */
const cache = new Map<string, Record<string, string>>();

async function loadLocaleAudio(language: string): Promise<Record<string, string>> {
  const cached = cache.get(language);
  if (cached) return cached;

  const { data, error } = await supabase
    .from("daily_recap_audio")
    .select("recap_id, audio_url")
    .eq("language", language)
    .eq("status", "ready")
    .limit(200);

  if (error) return {};
  const map: Record<string, string> = {};
  for (const row of data ?? []) {
    if (row.audio_url) map[row.recap_id] = row.audio_url;
  }
  cache.set(language, map);
  return map;
}

export function useRecapAudioMap(): {
  map: Record<string, string>;
  resolve: (recapId: string | null | undefined, fallback: string | null | undefined) => string | null;
} {
  const locale = useLocale();
  const [map, setMap] = useState<Record<string, string>>({});

  useEffect(() => {
    if (locale === "en") {
      setMap({});
      return;
    }
    let active = true;
    loadLocaleAudio(locale)
      .then((result) => {
        if (active) setMap(result);
      })
      .catch(() => undefined);
    return () => {
      active = false;
    };
  }, [locale]);

  return {
    map,
    resolve: (recapId, fallback) =>
      (recapId ? map[recapId] : null) ?? fallback ?? null,
  };
}
